'use client';

import { BadgeCheck, Clock, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

interface AccountStatusBannerProps {
  trialEndsAt: Date | null;
  verified: boolean;
  subscriptionEndsAt?: Date | null;
}

function calculateDaysRemaining(date: Date): number {
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('pl-PL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function AccountStatusBanner({
  trialEndsAt,
  verified,
  subscriptionEndsAt,
}: AccountStatusBannerProps) {
  const { t } = useTranslation('account');

  // Active subscription
  if (verified) {
    // Unlimited subscription
    if (!subscriptionEndsAt) {
      return (
        <StatusBanner variant="success">
          <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
          <span>Subskrypcja bezterminowa</span>
        </StatusBanner>
      );
    }

    // Time-limited subscription
    const daysRemaining = calculateDaysRemaining(subscriptionEndsAt);
    const isExpired = daysRemaining <= 0;
    const isEndingSoon = daysRemaining > 0 && daysRemaining <= 7;

    // Subscription expired
    if (isExpired) {
      return (
        <StatusBanner variant="error">
          <XCircle className="h-3.5 w-3.5 shrink-0" />
          <span>Subskrypcja wygasła</span>
        </StatusBanner>
      );
    }

    // Subscription ending soon
    if (isEndingSoon) {
      return (
        <StatusBanner variant="warning">
          <Clock className="h-3.5 w-3.5 shrink-0" />
          <span>
            {daysRemaining === 1
              ? 'Subskrypcja wygasa jutro'
              : `Subskrypcja wygasa za ${daysRemaining} dni`}
          </span>
        </StatusBanner>
      );
    }

    // Active subscription with future end date - show subtle info
    return (
      <StatusBanner variant="success">
        <BadgeCheck className="h-3.5 w-3.5 shrink-0" />
        <span>Subskrypcja aktywna do {formatDate(subscriptionEndsAt)}</span>
      </StatusBanner>
    );
  }

  // No trial started yet
  if (!trialEndsAt) {
    return null;
  }

  // Trial status
  const daysRemaining = calculateDaysRemaining(trialEndsAt);
  const isExpired = daysRemaining <= 0;
  const isLastDay = daysRemaining === 1;

  if (isExpired) {
    return (
      <StatusBanner variant="error">
        <XCircle className="h-3.5 w-3.5 shrink-0" />
        <span>{t('trialStatus.expired')}</span>
      </StatusBanner>
    );
  }

  if (isLastDay) {
    return (
      <StatusBanner variant="warning">
        <Clock className="h-3.5 w-3.5 shrink-0" />
        <span>{t('trialStatus.lastDay')}</span>
      </StatusBanner>
    );
  }

  return (
    <StatusBanner variant="info">
      <Clock className="h-3.5 w-3.5 shrink-0" />
      <span>{t('trialStatus.daysRemaining', { days: daysRemaining })}</span>
    </StatusBanner>
  );
}

function StatusBanner({
  variant,
  children,
}: {
  variant: 'info' | 'warning' | 'error' | 'success';
  children: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 rounded-md px-3 py-2 text-sm',
        variant === 'info' &&
          'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
        variant === 'warning' &&
          'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
        variant === 'error' &&
          'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300',
        variant === 'success' &&
          'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
      )}
    >
      {children}
    </div>
  );
}
